import type { SortingState } from '@tanstack/react-table';
import { formatDate } from '../../utils/stringHelpers';
import type { ReportingApp } from './types';

export type ReportingTabKey =
  | 'summary'
  | 'onboarding'
  | 'automatedReview'
  | 'externalMetrics'
  | 'appList'
  | 'individual';

export type ReportingPresetKey = '7d' | '30d' | '90d' | '1y' | 'custom';
export type ReportingLifecycleFilterValue = 'all' | ReportingApp['lifecycle'];

export const formatReportingLifecycleLabel = (
  lifecycle: ReportingApp['lifecycle'],
) => (lifecycle === 'new' ? 'New launch' : 'Version update');

export interface ReportingDateRangeState {
  fromInput: string;
  toInput: string;
  preset: ReportingPresetKey;
}

const toInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const createPresetDateRange = (
  preset: Exclude<ReportingPresetKey, 'custom'>,
) => {
  const endDate = new Date();
  const startDate = new Date(endDate);

  switch (preset) {
    case '7d':
      startDate.setDate(endDate.getDate() - 6);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 29);
      break;
    case '90d':
      startDate.setDate(endDate.getDate() - 89);
      break;
    case '1y':
      startDate.setFullYear(endDate.getFullYear() - 1);
      startDate.setDate(startDate.getDate() + 1);
      break;
  }

  return {
    fromInput: toInputDate(startDate),
    toInput: toInputDate(endDate),
    preset,
  } satisfies ReportingDateRangeState;
};

export const createInitialDateRange = () => createPresetDateRange('30d');

export const toStartOfLocalDayIso = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

export const toEndOfLocalDayIso = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

const readSortValue = (app: ReportingApp, columnId: string) => {
  switch (columnId) {
    case 'dappName':
      return app.dappName.toLowerCase();
    case 'category':
      return app.category.toLowerCase();
    case 'storePrimaryCategory':
      return app.storePrimaryCategory?.toLowerCase() ?? '';
    case 'storePublisherName':
      return app.storeListing?.publisherName?.toLowerCase() ?? '';
    case 'lifecycle':
      return app.lifecycle;
    case 'platformType':
      return app.platformType;
    case 'appSizeBytes':
      return app.appSizeBytes ?? -1;
    case 'storeInstallSizeBytes':
      return app.storeListing?.installSizeBytes ?? -1;
    case 'storeVersionCode':
      return app.storeListing?.versionCode ?? -1;
    case 'storeUpdatedAt':
      return app.storeListing?.updatedOn ?? -1;
    case 'updatesInPeriod':
      return app.updatesInPeriod;
    case 'firstPublishedAt':
      return app.firstPublishedAt ?? -1;
    case 'latestPublishedAtInRange':
      return app.latestPublishedAtInRange ?? -1;
    case 'reviews':
      return app.reviews ?? -1;
    case 'averageReviewScore':
      return app.averageReviewScore ?? -1;
    case 'oneStarReviews':
      return app.oneStarReviews ?? -1;
    case 'twoStarReviews':
      return app.twoStarReviews ?? -1;
    case 'threeStarReviews':
      return app.threeStarReviews ?? -1;
    case 'fourStarReviews':
      return app.fourStarReviews ?? -1;
    case 'fiveStarReviews':
      return app.fiveStarReviews ?? -1;
    case 'totalInstalls':
      return app.totalInstalls ?? -1;
    case 'totalUninstalls':
      return app.totalUninstalls ?? -1;
    case 'netInstalls':
      return app.netInstalls ?? -1;
    default:
      return app.latestPublishedAtInRange ?? app.latestPublishedAt ?? -1;
  }
};

export const sortReportingApps = (
  apps: ReportingApp[],
  sorting: SortingState,
) => {
  const activeSort = sorting[0];

  if (!activeSort) {
    return apps;
  }

  return [...apps].sort((left, right) => {
    const leftValue = readSortValue(left, activeSort.id);
    const rightValue = readSortValue(right, activeSort.id);

    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      const comparison = leftValue.localeCompare(rightValue);
      return activeSort.desc ? comparison * -1 : comparison;
    }

    if (leftValue === rightValue) {
      return left.dappName.localeCompare(right.dappName);
    }

    const comparison =
      Number(leftValue ?? -1) > Number(rightValue ?? -1) ? 1 : -1;
    return activeSort.desc ? comparison * -1 : comparison;
  });
};

export const sortReportingAppsAlphabetically = (apps: ReportingApp[]) => {
  return [...apps].sort((left, right) =>
    left.dappName.localeCompare(right.dappName, undefined, {
      sensitivity: 'base',
    }),
  );
};

export const filterReportingApps = (
  apps: ReportingApp[],
  {
    search,
    category,
    lifecycle = 'all',
  }: {
    search: string;
    category: string;
    lifecycle?: ReportingLifecycleFilterValue;
  },
) => {
  const normalizedSearch = search.trim().toLowerCase();

  return apps.filter((app) => {
    const matchesSearch =
      normalizedSearch.length === 0 ||
      app.dappName.toLowerCase().includes(normalizedSearch) ||
      app.packageName?.toLowerCase()?.includes(normalizedSearch) ||
      app.storeListing?.displayName
        ?.toLowerCase()
        ?.includes(normalizedSearch) ||
      app.storeListing?.publisherName
        ?.toLowerCase()
        ?.includes(normalizedSearch) ||
      app.storeListing?.auxStatus?.toLowerCase()?.includes(normalizedSearch) ||
      app.storePrimaryCategory?.toLowerCase()?.includes(normalizedSearch);
    const matchesCategory =
      category === 'all' ||
      app.category.toLowerCase() === category.toLowerCase();
    const matchesLifecycle = lifecycle === 'all' || app.lifecycle === lifecycle;

    return matchesSearch && matchesCategory && matchesLifecycle;
  });
};

export const formatNullableCount = (
  value: number | null,
  fallback = 'Source needed',
) => (value === null ? fallback : value.toLocaleString());

export const formatReviewScore = (
  value: number | null,
  reviewCount: number | null,
  missingFallback = 'Source needed',
) => {
  if (reviewCount === null) {
    return missingFallback;
  }

  if (reviewCount === 0 || value === null) {
    return 'No ratings';
  }

  return value.toFixed(2);
};

export const formatNullableDate = (
  value: number | null,
  fallback = 'No publish date',
) => (value === null ? fallback : formatDate(value));
